// src/services/profile/initialization.ts

import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useDebugStore } from "../../store/debugStore";
import { UserDatabaseStructure } from "../database/userStructure";
import type { UserProfile } from "../../types/user";
import { UniversalDirectoryManager } from "../../services/storage/universalDirectoryManager";

export class ProfileInitializationService {
  /**
   * Verifies if the user profile exists. If not, initializes a default profile.
   * @param userId - The unique ID of the user.
   * @param email - The user's email address.
   * @param name - The user's full name.
   * @returns A promise that resolves to true if successful, false otherwise.
   */
  static async verifyAndInitializeProfile(
    userId: string,
    email: string,
    name: string,
  ): Promise<boolean> {
    // Get the function to add debug messages
    const addDebugMessage = useDebugStore.getState().addMessage;

    try {
      // 1. Ensure the base user directory (creates .collection doc at "users/{userId}")
      const basePath = UniversalDirectoryManager.getPath({
        userId,
        contentType: "user",
      });
      await UniversalDirectoryManager.verifyDirectory(basePath, "user");

      // 2. Reference the user's profile document in Firestore
      const profileRef = doc(
        db,
        UserDatabaseStructure.getProfilePath(userId),
        "data",
      );
      const profileDoc = await getDoc(profileRef);

      // 3. If the profile document doesn't exist, create a default profile
      if (!profileDoc.exists()) {
        addDebugMessage("info", "Creating default user profile");

        // Split the full name into firstName and lastName
        const nameParts = name.trim().split(" ");
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";

        // Create a default user profile with required fields
        const defaultProfile: Omit<UserProfile, "createdAt" | "updatedAt"> & {
          createdAt: Timestamp;
          updatedAt: Timestamp;
        } = {
          id: userId,
          email,
          name,
          firstName,
          lastName,
          role: "user",
          phone: "",
          alternativeEmail: "",
          dateOfBirth: "",
          gender: "prefer-not-to-say",
          address: {
            street: "",
            city: "",
            state: "",
            zip: "",
            country: "",
          },
          emergencyContact: {
            name: "",
            phone: "",
            relationship: "",
          },
          medicalInfo: {
            bloodType: "",
            allergies: "",
            conditions: "",
            medications: "",
            healthFund: "",
            memberNumber: "",
            healthFundId: "",
            cardNumber: "",
            medicareNumber: "",
            medicareIrn: "",
            medicareExpiry: "",
            concessionNumberExpiry: "",
            ndisNumber: "",
          },
          socialMedia: {
            instagram: "",
            tiktok: "",
            facebook: "",
            linkedin: "",
          },
          membership: {
            tier: "free",
            startDate: new Date(),
            autoRenew: false,
            features: {
              maxVCards: 3,
              maxDevices: 3,
              customBranding: false,
              analytics: false,
              bulkGeneration: false,
            },
          },
          createdAt: serverTimestamp() as Timestamp,
          updatedAt: serverTimestamp() as Timestamp,
        };

        // Save the default profile to Firestore
        await setDoc(profileRef, defaultProfile, { merge: true });
        addDebugMessage("success", "Default user profile created successfully");
        return true;
      }

      // If the profile already exists
      addDebugMessage("info", "User profile already exists");
      return true;
    } catch (error) {
      // Log any errors
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      addDebugMessage(
        "error",
        `Failed to verify/initialize profile: ${errorMessage}`,
      );
      return false;
    }
  }

  /**
   * Fetches the user's profile from Firestore.
   * @param userId - The unique ID of the user.
   * @returns A promise that resolves to the UserProfile or null if not found.
   */
  static async fetchUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const profileRef = doc(
        db,
        UserDatabaseStructure.getProfilePath(userId),
        "data",
      );
      const profileDoc = await getDoc(profileRef);
      return profileDoc.exists() ? (profileDoc.data() as UserProfile) : null;
    } catch {
      return null;
    }
  }
}
